import React, { useState, useEffect, useMemo } from 'react';
import { Camera, Upload, FileText, UserCircle, X, Coins, RefreshCcw, MessageSquare } from 'lucide-react';
import { menuAnalysis, userProfile, analysisService } from '../api';
import { followUpAnalysis } from '../api/menu';
import ReactMarkdown from 'react-markdown';

const AppPage = ({ user, setCurrentPage }) => {
  const [menuImage, setMenuImage] = useState(null);
  const [dietaryNotes, setDietaryNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState('camera');
  const [recommendations, setRecommendations] = useState([]);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [ignoreProfilePreferences, setIgnoreProfilePreferences] = useState(false);
  const [dietaryPreferences, setDietaryPreferences] = useState([]);
  const [analysisData, setAnalysisData] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState('');
  const [followUpType, setFollowUpType] = useState('new_analysis');
  const [followUpPrompt, setFollowUpPrompt] = useState('');
  const followUpSectionRef = React.useRef(null);  // Reference for scrolling to follow-up section

  // Array of food-related loading quotes - memoized to prevent recreation on every render
  const loadingQuotes = useMemo(() => [
    "Cooking up something good...",
    "Sifting through the menu options...",
    "Tasting the possibilities...",
    "Finding the perfect dish for you...",
    "Analyzing flavors and ingredients...",
    "Preparing your personalized recommendations...",
    "Whisking up the best choices...",
    "Stirring the pot of possibilities...",
    "Letting the recommendations simmer...",
    "Seasoning your results to perfection...",
    "Stirring the pot…",
    "Plating your recommendations…",
    "Seasoning the suggestions…",
    "Simmer down… almost there!",
    "Fetching the secret sauce…",
    "Roasting ideas to perfection…",
    "Whipping up your flavor profile…",
    "Slicing and dicing preferences…",
    "Preheating the algorithm…",
    "Serving AI al dente…",
    "Marinating your menu…",
    "Checking the pantry…",
    "Loading… just a few more shakes of paprika.",
    "Taste testing your results…",
    "Tossing your ingredients together…",
    "Don't go bacon my heart… we're almost ready!",
    "Grating data like parmesan…",
    "Sautéing some suggestions…",

    // Restaurant & Experience Oriented
    "Finding you a table at flavor town…",
    "Scanning today's specials…",
    "Serving culinary inspiration…",
    "Letting the chef get creative…",
    "Spinning the spice wheel…",
    "Calling the sommelier…",
    "Warming up the ovens…",
    "Checking what's fresh today…",
    "We're buttering your bread…",
    "Polishing your plate…"
  ], []); // Empty dependency array means this array will only be created once

  // Select a random loading quote when loading state changes
  useEffect(() => {
    if (isLoading) {
      const randomIndex = Math.floor(Math.random() * loadingQuotes.length);
      setLoadingQuote(loadingQuotes[randomIndex]);
    }
  }, [isLoading, loadingQuotes]); // Added loadingQuotes to dependency array

  // Fetch dietary preferences on component mount
  React.useEffect(() => {
    if (activeTab === 'preferences') {
      fetchDietaryPreferences();
    }
  }, [activeTab]);

  // Fetch user's dietary preferences
  const fetchDietaryPreferences = async () => {
    try {
      const [prefsResponse, restrictionsResponse] = await Promise.all([
        userProfile.getDietaryPreferences(),
        userProfile.getDietaryRestrictions()
      ]);

      // Combine preferences and restrictions for display
      const preferences = prefsResponse.data || {};
      const restrictions = restrictionsResponse.data || [];

      const preferencesList = [];

      // Add diet type if available
      if (preferences.diet_type) {
        preferencesList.push({
          id: 'diet-type',
          name: preferences.diet_type.charAt(0).toUpperCase() + preferences.diet_type.slice(1),
          color: 'green'
        });
      }

      // Add protein preference if available
      if (preferences.protein_preference) {
        preferencesList.push({
          id: 'protein',
          name: preferences.protein_preference === 'high' ? 'High Protein' :
            preferences.protein_preference === 'low' ? 'Low Protein' : 'Moderate Protein',
          color: 'blue'
        });
      }

      // Add carb preference if available
      if (preferences.carb_preference) {
        preferencesList.push({
          id: 'carb',
          name: preferences.carb_preference === 'low' ? 'Low Carb' :
            preferences.carb_preference === 'high' ? 'High Carb' : 'Moderate Carb',
          color: 'yellow'
        });
      }

      // Add dietary restrictions
      if (restrictions.length > 0) {
        restrictions.forEach(restriction => {
          preferencesList.push({
            id: restriction.id,
            name: restriction.name,
            color: 'red'
          });
        });
      }

      setDietaryPreferences(preferencesList);
    } catch (error) {
      console.error('Error fetching dietary preferences:', error);
      // Use default empty array if API call fails
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (32 MB limit = 32 * 1024 * 1024 bytes)
      const maxSizeBytes = 32 * 1024 * 1024;
      if (selectedFile.size > maxSizeBytes) {
        setError('File size exceeds the maximum limit of 32 MB. Please choose a smaller file.');
        // Clear file input
        e.target.value = '';
        return;
      }

      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError(''); // Clear any previous error

      // Only create a preview for image files, not PDFs
      if (selectedFile.type.startsWith('image/')) {
        setMenuImage(URL.createObjectURL(selectedFile));
      } else if (selectedFile.type === 'application/pdf') {
        // For PDFs, we'll just show a generic PDF icon or text indicator
        setMenuImage('pdf');
      }
    }
  };

  // Function to handle menu analysis
  const analyzeMenu = async () => {
    if (!file && activeTab !== 'camera') {
      setError('Please upload a menu image or use the camera to take a photo.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Create a FormData object to send the file
      const formData = new FormData();
      if (file) {
        console.log('File object:', file);
        formData.append('menu_file', file);
      }
      formData.append('dietary_notes', dietaryNotes);
      formData.append('restaurant', ''); // Adding empty restaurant field
      formData.append('ignore_preferences', ignoreProfilePreferences ? 'true' : 'false'); // Add preference flag

      // Debug - check formData contents
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      // Call the API to analyze the menu
      console.log('Sending formData to API...');
      const response = await menuAnalysis.analyzeMenu(formData);
      console.log('API response:', response);

      // Check if response is the data object itself or if it contains a data property
      const responseData = response.data || response;
      console.log('Response data being used:', responseData);

      setRecommendations(responseData.recommendations || []);
      setAnalysisData(responseData);
      setShowResults(true);
    } catch (error) {
      console.error('Error analyzing menu:', error);
      setError('Failed to analyze menu. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset the app
  const resetApp = () => {
    setMenuImage(null);
    setFile(null);
    setFileName('');
    setDietaryNotes('');
    setShowResults(false);
    setActiveTab('camera');
    setError('');
    setAnalysisData(null);
    setIgnoreProfilePreferences(false);
    setFollowUpPrompt('');
  };
  
  // Scroll to follow-up section
  const scrollToFollowUp = () => {
    if (followUpSectionRef.current) {
      followUpSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      // Focus on the textarea after scrolling
      setTimeout(() => {
        const textarea = followUpSectionRef.current.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 500);
    }
  };
  
  // Handle follow-up analysis or questions
  const handleFollowUp = async () => {
    if (!followUpPrompt.trim()) {
      setError('Please enter a prompt or question for the follow-up');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Prepare data for follow-up request
      const followUpData = {
        original_analysis_id: analysisData.id,
        follow_up_type: followUpType,
        prompt: followUpPrompt
      };
      
      // Call the API for follow-up analysis
      const response = await followUpAnalysis(followUpData);
      
      // Update state with new results
      setRecommendations(response.recommendations || []);
      setAnalysisData(response);
      setFollowUpPrompt(''); // Clear the prompt
    } catch (error) {
      console.error('Error with follow-up:', error);
      setError('Failed to process follow-up. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Check for selected analysis in localStorage on component mount
  useEffect(() => {
    const selectedAnalysisId = localStorage.getItem('selected_analysis_id');
    if (selectedAnalysisId) {
      fetchExistingAnalysis(selectedAnalysisId);
      // Clear the stored ID after loading
      localStorage.removeItem('selected_analysis_id');
    }
  }, []);

  // Fetch an existing analysis by ID
  const fetchExistingAnalysis = async (analysisId) => {
    try {
      setIsLoading(true);
      const response = await analysisService.getAnalysisDetails(analysisId);
      const analysisData = response.data;
      
      setRecommendations(analysisData.recommendations || []);
      setAnalysisData(analysisData);
      // Set dietary notes from the saved query text
      if (analysisData.query_text) {
        setDietaryNotes(analysisData.query_text);
      }
      setShowResults(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching analysis:', error);
      setError('Failed to load the selected analysis. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Menu Analyzer</h1>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
            <div className="animate-spin mx-auto mb-4 h-12 w-12">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="#10B981" strokeWidth="4"></circle>
                <path className="opacity-75" fill="#10B981" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-3">{loadingQuote}</h3>
            <p className="text-gray-500">Please wait while we analyze your menu...</p>
          </div>
        </div>
      )}

      {!showResults ? (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b">
            <button
              className={`px-4 py-2 w-1/3 text-center ${activeTab === 'camera' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('camera')}
            >
              <Camera className="inline mr-1 h-4 w-4" />
              Camera
            </button>
            <button
              className={`px-4 py-2 w-1/3 text-center ${activeTab === 'upload' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('upload')}
            >
              <Upload className="inline mr-1 h-4 w-4" />
              Upload
            </button>
            <button
              className={`px-4 py-2 w-1/3 text-center ${activeTab === 'preferences' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500 font-medium' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('preferences')}
            >
              <UserCircle className="inline mr-1 h-4 w-4" />
              Preferences
            </button>
          </div>

          {/* Tab content */}
          <div className="p-6">
            {activeTab === 'upload' && (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <div className="w-full max-w-lg">
                    <label className={`flex flex-col items-center px-4 py-6 bg-white rounded-lg shadow-lg tracking-wide border border-dashed cursor-pointer hover:bg-emerald-50 ${menuImage ? 'border-gray-300 text-gray-700' : 'border-emerald-500 text-emerald-500'}`}>
                      {menuImage ? (
                        <>
                          <div className="flex items-center">
                            <FileText className="w-6 h-6 mr-2" />
                            <span className="text-base leading-normal">File selected - Click to replace</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8" />
                          <span className="mt-2 text-base leading-normal">Upload menu image or PDF</span>
                        </>
                      )}
                      <input type='file' className="hidden" accept="image/*,.pdf" onChange={handleFileChange} />
                    </label>
                  </div>
                </div>

                {menuImage && (
                  <div className="flex justify-center mt-4">
                    <div className="relative border border-gray-200 rounded-lg overflow-hidden">
                      {menuImage === 'pdf' ? (
                        <div className="flex justify-center items-center h-40 w-64 bg-gray-100 p-4">
                          <div className="text-center">
                            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">{fileName || 'PDF Document'}</p>
                          </div>
                        </div>
                      ) : (
                        <img src={menuImage} alt="Uploaded menu" className="max-h-64 w-auto" />
                      )}
                      <button
                        onClick={() => {
                          setMenuImage(null);
                          setFile(null);
                          setFileName('');
                        }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                      >
                        <X className="h-5 w-5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'camera' && (
              <div className="space-y-6">
                <div className="flex justify-center">
                  {menuImage && menuImage !== 'pdf' ? (
                    <div className="relative border border-gray-200 rounded-lg overflow-hidden w-full max-w-lg">
                      <img src={menuImage} alt="Captured menu" className="max-h-64 w-auto mx-auto" />
                      <button
                        onClick={() => {
                          setMenuImage(null);
                          setFile(null);
                          setFileName('');
                        }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                      >
                        <X className="h-5 w-5 text-gray-600" />
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-200 rounded-lg overflow-hidden w-full max-w-lg aspect-video flex items-center justify-center">
                      <button
                        className="bg-emerald-600 text-white py-3 px-6 rounded-full font-medium hover:bg-emerald-700 transition-colors flex items-center"
                        onClick={() => {
                          // Access device camera using the device's file picker
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.capture = 'environment';
                          input.onchange = (e) => handleFileChange(e);
                          input.click();
                        }}
                      >
                        <Camera className="h-5 w-5 mr-2" />
                        Take Photo
                      </button>
                    </div>
                  )}
                </div>

                <p className="text-sm text-gray-500 text-center">
                  {menuImage ? 'Photo captured. You can analyze the menu or take another photo.' : 'Your browser will request camera access. Position the menu clearly in frame.'}
                </p>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-lg mb-2">Your Saved Dietary Preferences</h3>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {dietaryPreferences.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {dietaryPreferences.map(pref => (
                          <span
                            key={pref.id}
                            className={`px-3 py-1 bg-${pref.color}-100 text-${pref.color}-800 rounded-full text-sm font-medium`}
                          >
                            {pref.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 mb-4">
                        No dietary preferences saved yet. Update your preferences in your profile.
                      </p>
                    )}
                    <button
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                      onClick={() => setCurrentPage('profile')}
                    >
                      Edit saved preferences
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <label htmlFor="dietary-notes" className="block text-sm font-medium text-gray-700 mb-1">
                What are you looking for today?
              </label>
              <textarea
                id="dietary-notes"
                className="shadow-sm focus:ring-emerald-500 focus:border-emerald-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="E.g., 'I'm craving something with curry but need it to be gluten-free' or 'Looking for a high-protein meal that's under 600 calories'"
                rows={4}
                value={dietaryNotes}
                onChange={(e) => setDietaryNotes(e.target.value)}
                maxLength={500}
              ></textarea>
              <div className="mt-1 text-xs text-gray-500 flex justify-end">
                {dietaryNotes.length}/500 characters
              </div>
            </div>

            <div className="mt-6">
              <div className="flex justify-end items-center space-x-4">
                <div className="flex items-center">
                  <input
                    id="ignore-preferences"
                    name="ignore-preferences"
                    type="checkbox"
                    checked={ignoreProfilePreferences}
                    onChange={(e) => setIgnoreProfilePreferences(e.target.checked)}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                  <label htmlFor="ignore-preferences" className="ml-2 text-sm text-gray-600">
                    Ignore profile preferences
                  </label>
                </div>
                <button
                  onClick={analyzeMenu}
                  disabled={((!menuImage && activeTab !== 'camera') || isLoading)}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 ${((!menuImage && activeTab !== 'camera') || isLoading) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-emerald-700'}`}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analyzing...
                    </>
                  ) : (
                    <div className="flex items-center">
                      Analyze Menu
                      <div className="ml-1.5 bg-yellow-400 rounded-full w-5 h-5 flex items-center justify-center border-2 border-yellow-500 shadow-sm">
                        <span className="text-[11px] font-bold text-gray-800">1</span>
                      </div>
                    </div>
                  )}
                </button>
              </div>
              <div className="flex justify-end mt-2">
                <p className="text-xs text-gray-500 opacity-75">
                  AI can make mistakes; verify results before using or acting on them. Please review our <span 
                    className="underline cursor-pointer hover:text-emerald-600"
                    onClick={() => setCurrentPage('legal')}
                  >
                    terms of service
                  </span> for more information.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Your Personalized Recommendations</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={scrollToFollowUp}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Follow-up
                  </button>
                  <button
                    onClick={resetApp}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Analyze Another Menu
                  </button>
                </div>
              </div>

              {/* Restaurant Name Display */}
              {analysisData && analysisData.restaurant && (
                <div className="mt-3 mb-2">
                  <h3 className="text-lg font-medium text-gray-700">{analysisData.restaurant}</h3>
                </div>
              )}

              {/* Token usage information */}
              {analysisData && analysisData.tokens_consumed !== undefined && (
                <div className="mt-2 mb-2">
                  <p className="text-xs text-gray-500">
                    <span className="font-semibold">Tokens consumed:</span> {analysisData.tokens_consumed.toLocaleString()}
                  </p>
                </div>
              )}

              {/* Menu reference - More compact design */}
              {analysisData && analysisData.image_url && (
                <div className="mt-2 flex items-center">
                  <div className="flex-shrink-0 mr-3">
                    {analysisData.image_url.toLowerCase().endsWith('.pdf') ? (
                      <div className="flex justify-center items-center h-10 w-10 bg-gray-100 rounded">
                        <FileText className="h-6 w-6 text-gray-400" />
                      </div>
                    ) : (
                      <div className="h-10 w-10 bg-gray-100 rounded overflow-hidden">
                        <img
                          src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${analysisData.image_url}`}
                          alt="Menu"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Based on the menu you uploaded
                      <button
                        onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${analysisData.image_url}`, '_blank')}
                        className="ml-1 text-xs text-emerald-600 hover:text-emerald-700 underline"
                      >
                        (view full document)
                      </button>
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-2">
                <p className="text-sm text-gray-500">Based on your dietary preferences and today's criteria:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {dietaryPreferences.slice(0, 3).map(pref => (
                    <span
                      key={pref.id}
                      className={`px-3 py-1 bg-${pref.color}-100 text-${pref.color}-800 rounded-full text-xs font-medium`}
                    >
                      {pref.name}
                    </span>
                  ))}
                  {dietaryNotes && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      "{dietaryNotes.length > 100 ? dietaryNotes.substring(0, 100) + '...' : dietaryNotes}"
                    </span>
                  )}
                </div>
              </div>
              

            </div>

            <div className="p-6">
              <div className="space-y-8">
                {recommendations.length > 0 ? recommendations.map((item, index) => (
                  <div key={`${item.dish_name}-${index}`} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-emerald-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="font-medium text-lg text-gray-900">{item.dish_name}</h3>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-500 mr-2">Match Score</span>
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${item.match_score >= 90 ? 'bg-green-100 text-green-800' :
                            item.match_score >= 80 ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                          }`}>
                          {item.match_score}%
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      {item.dish_name === "Answer to your question" ? (
                        <div className="prose max-w-none text-gray-700 mb-4">
                          <ReactMarkdown>{item.description}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-gray-700 mb-4">{item.description}</p>
                      )}

                      {/* Nutritional information section removed to prevent displaying potentially hallucinated data */}

                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Why We Recommend This</h4>
                        <p className="text-sm text-gray-600">
                          {item.why_recommended || item.explanation || "This dish aligns with your dietary preferences."}
                        </p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      No recommendations available. This could be due to insufficient information or no appropriate matches.
                    </p>
                  </div>
                )}
                
                {/* Follow-up Analysis Form - Always visible at the bottom */}
                <div ref={followUpSectionRef} className="mt-8 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-md font-medium text-blue-700 mb-2">
                    Follow-up with this menu
                  </h3>
                  
                  <div className="flex space-x-2 mb-3">
                    <button
                      onClick={() => setFollowUpType('new_analysis')}
                      className={`px-3 py-1 text-xs rounded-full ${followUpType === 'new_analysis' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                    >
                      <RefreshCcw className="h-3 w-3 inline mr-1" />
                      New Analysis
                    </button>
                    <button
                      onClick={() => setFollowUpType('question')}
                      className={`px-3 py-1 text-xs rounded-full ${followUpType === 'question' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                    >
                      <MessageSquare className="h-3 w-3 inline mr-1" />
                      Ask a Question
                    </button>
                    <div className="ml-auto flex items-center">
                      <Coins className="h-4 w-4 text-yellow-500 mr-1" />
                      <span className="text-xs text-gray-500">Uses 1 credit</span>
                    </div>
                  </div>
                  
                  <textarea
                    className="w-full border border-blue-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder={followUpType === 'new_analysis' 
                      ? "Enter new criteria for analyzing this menu (e.g., 'Find low-carb options with seafood')" 
                      : "Ask a question about the analysis results (e.g., 'Which dish has the least sodium?')"}
                    rows={3}
                    value={followUpPrompt}
                    onChange={(e) => setFollowUpPrompt(e.target.value)}
                  />
                  
                  <div className="flex justify-end mt-2 space-x-2">
                    <button
                      onClick={handleFollowUp}
                      disabled={isLoading || !followUpPrompt.trim()}
                      className={`px-3 py-1 text-xs text-white rounded ${isLoading || !followUpPrompt.trim() ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      {isLoading ? 'Processing...' : 'Submit'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppPage;
